<template>
  <section
id="dashboard-home"
class="modern-dashboard"
>
    <b-row class="match-height">

      <b-col
cols="12"
class="animate-slide-up"
style="animation-delay: 0.02s;"
>
        <b-card
no-body
:class="['greeting-panel', timeSceneClass]"
>
          <div class="greeting-panel__content">
            <div class="greeting-copy">
              <span class="greeting-kicker">{{ formattedDate }}</span>
              <h1>{{ greetingText }}, {{ firstName }}</h1>
              <p>{{ greetingMessage }}</p>
              <div class="greeting-clock">
                <span>{{ currentTime }}</span>
                <small>{{ timezoneLabel }}</small>
              </div>
            </div>

            <div
              class="sky-stage"
              aria-hidden="true"
            >
              <template v-if="isNightScene">
                <div class="night-sky">
                  <span class="star star-1" />
                  <span class="star star-2" />
                  <span class="star star-3" />
                  <span class="star star-4" />
                  <span class="star star-5" />
                  <span class="moon" />
                  <span class="moon-glow" />
                  <span class="night-haze night-haze-1" />
                  <span class="night-haze night-haze-2" />
                </div>
              </template>
              <template v-else>
                <div class="day-sky">
                  <span class="sun-rays" />
                  <span class="sun-core" />
                  <span class="sun-halo sun-halo-1" />
                  <span class="sun-halo sun-halo-2" />
                  <span class="cloud cloud-1" />
                  <span class="cloud cloud-2" />
                  <span class="cloud cloud-3" />
                  <span class="light-streak light-streak-1" />
                  <span class="light-streak light-streak-2" />
                </div>
              </template>
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
cols="12"
class="animate-slide-up"
style="animation-delay: 0.04s;"
>
        <b-card
no-body
class="exchange-widget mb-3"
>
          <div class="exchange-widget__head">
            <div class="exchange-widget__title">
              <span class="exchange-widget__icon">
                <feather-icon
                  icon="DollarSignIcon"
                  size="16"
                />
              </span>
              <div>
                <h3>Currency Rates</h3>
                <small>Updated {{ exchangeUpdatedLabel }}</small>
              </div>
            </div>
            <button
              type="button"
              class="exchange-widget__refresh"
              :disabled="exchangeLoading"
              title="Refresh rates"
              @click="fetchExchangeRates"
            >
              <b-spinner
                v-if="exchangeLoading"
                small
              />
              <feather-icon
                v-else
                icon="RefreshCwIcon"
                size="15"
              />
            </button>
          </div>

          <div class="exchange-rates-list">
            <template v-if="exchangeInitialLoading">
              <div
                v-for="n in 5"
                :key="`fx-skel-${n}`"
                class="exchange-rate-row"
              >
                <b-skeleton
                  type="avatar"
                  width="40px"
                  height="40px"
                />
                <div class="exchange-rate-row__info">
                  <b-skeleton
                    width="65%"
                    height="14px"
                    class="mb-1"
                  />
                  <b-skeleton
                    width="35%"
                    height="10px"
                  />
                </div>
                <b-skeleton
                  width="90px"
                  height="20px"
                />
                <b-skeleton
                  width="70px"
                  height="16px"
                />
              </div>
            </template>
            <template v-else>
              <div
                v-for="option in exchangeOptions"
                :key="option.code"
                class="exchange-rate-row"
              >
                <span
                  class="exchange-rate-row__flag"
                  :style="{ color: option.color, background: option.color + '1a' }"
                >
                  {{ option.flag }}
                </span>

                <div class="exchange-rate-row__info">
                  <strong>{{ quoteInIdr ? `1 ${option.code}` : '1 IDR' }} → {{ quoteInIdr ? 'Rp IDR' : option.code }}</strong>
                  <span
                    class="exchange-rate-row__change"
                    :class="pctChange(option.code) >= 0 ? 'is-up' : 'is-down'"
                  >
                    <feather-icon
                      :icon="pctChange(option.code) >= 0 ? 'TrendingUpIcon' : 'TrendingDownIcon'"
                      size="11"
                    />
                    {{ pctChange(option.code) >= 0 ? '+' : '' }}{{ pctChange(option.code).toFixed(2) }}%
                    <small>· {{ trendLabel(option.code) }}</small>
                  </span>
                </div>

                <svg
                  v-if="sparklinePoints(option.code)"
                  viewBox="0 0 100 32"
                  class="exchange-rate-row__spark"
                  preserveAspectRatio="none"
                >
                  <polyline
                    :points="sparklinePoints(option.code)"
                    fill="none"
                    :stroke="option.color"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <circle
                    v-if="lastPoint(option.code)"
                    :cx="lastPoint(option.code).x"
                    :cy="lastPoint(option.code).y"
                    r="2.5"
                    :fill="option.color"
                  />
                </svg>
                <span
                  v-else
                  class="exchange-rate-row__spark exchange-rate-row__spark--empty"
                />

                <strong class="exchange-rate-row__value">{{ formattedRate(option.code) }}</strong>
              </div>
            </template>
          </div>

          <div class="exchange-widget__foot">
            <span>Source: exchangerate-api.com</span>
            <span>Auto-refresh: manual</span>
            <button
              type="button"
              class="exchange-widget__flip"
              @click="quoteInIdr = !quoteInIdr"
            >
              Tap to flip: {{ quoteInIdr ? 'IDR → foreign' : 'foreign → IDR' }}
            </button>
          </div>
        </b-card>
      </b-col>

      <b-col
cols="12"
class="animate-slide-up"
style="animation-delay: 0.05s;"
>
        <b-card
class="modern-card border-left-primary mb-3"
body-class="p-3"
>
          <div class="d-flex align-items-center">
            <feather-icon
icon="ActivityIcon"
size="22"
class="text-primary mr-1"
/>
            <span class="text-primary font-weight-bolder header-title">
              Live Procurement Operations
            </span>
          </div>
          <p class="text-muted small mb-0 mt-1 font-weight-medium">
            {{ information || 'System routing is optimal. Viewing consolidated data from all active branches.' }}
          </p>
        </b-card>
      </b-col>

      <b-col
cols="12"
class="mb-3"
>
        <shimmer-card-grid
          v-if="loadingStats"
          :count="6"
        />
        <b-row
          v-else
          class="match-height"
        >

          <b-col
lg="7"
md="12"
class="mb-3 mb-lg-0 animate-slide-up"
style="animation-delay: 0.1s;"
>
            <b-card
no-body
class="modern-card h-100 p-3"
>
              <h5 class="font-weight-bolder mb-3 text-dark">
Procurement Workflow Status
</h5>

              <div class="d-flex justify-content-between align-items-center position-relative w-100 mt-2 flex-grow-1 px-lg-4">
                <div class="workflow-line" />

                <div class="text-center workflow-node">
                  <div class="node-circle border-primary">
                    <feather-icon
icon="FileTextIcon"
size="24"
class="text-primary"
/>
                  </div>
                  <h4 class="mt-2 font-weight-bolder text-dark mb-0">
{{ pendingMR }}
</h4>
                  <span class="small text-muted font-weight-medium">Pending MR</span>
                </div>

                <div class="text-center workflow-node">
                  <div class="node-circle node-circle-lg border-success shadow-sm">
                    <h3 class="mb-0 text-success font-weight-bolder">
94%
</h3>
                  </div>
                  <h6 class="mt-2 font-weight-bolder text-dark mb-0">
Process Health
</h6>
                  <span class="small text-muted font-weight-medium">On-time Routing</span>
                </div>

                <div class="workflow-node output-node bg-white border rounded shadow-sm p-2">
                  <h6 class="small font-weight-bolder text-muted mb-1 border-bottom pb-50 text-left">
Active Requests
</h6>
                  <div class="d-flex justify-content-between small mb-50">
                    <span class="text-secondary">PR Asset</span>
                    <span class="font-weight-bolder text-dark">{{ pendingPRLocal }}</span>
                  </div>
                  <div class="d-flex justify-content-between small mb-50">
                    <span class="text-secondary">PR Supp</span>
                    <span class="font-weight-bolder text-dark">{{ pendingPRSupplies }}</span>
                  </div>
                  <div class="d-flex justify-content-between small">
                    <span class="text-secondary">PR HO</span>
                    <span class="font-weight-bolder text-dark">{{ pendingPRHO }}</span>
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>

          <b-col
lg="5"
md="12"
>
            <b-row>
              <b-col
sm="6"
class="mb-3 animate-slide-up"
style="animation-delay: 0.15s;"
>
                <b-card
no-body
class="modern-card p-2 d-flex flex-column h-100"
>
                  <span class="text-muted font-weight-medium small">Pending MR Volume</span>
                  <div class="d-flex justify-content-between align-items-end mt-50 mb-1">
                    <h2 class="mb-0 font-weight-bolder text-dark">
{{ pendingMR }}
</h2>
                    <span class="text-success small font-weight-bolder bg-light-success px-50 py-25 rounded">
                      <feather-icon
icon="TrendingUpIcon"
size="12"
/> +5%
                    </span>
                  </div>
                  <vue-apex-charts
type="area"
height="60"
:options="sparklineOptions"
:series="sparklineMR"
/>
                </b-card>
              </b-col>

              <b-col
sm="6"
class="mb-3 animate-slide-up"
style="animation-delay: 0.2s;"
>
                <b-card
no-body
class="modern-card p-2 d-flex flex-column h-100"
>
                  <span class="text-muted font-weight-medium small">PR Supplies Load</span>
                  <div class="d-flex justify-content-between align-items-end mt-50 mb-1">
                    <h2 class="mb-0 font-weight-bolder text-dark">
{{ pendingPRSupplies }}
</h2>
                    <span class="text-danger small font-weight-bolder bg-light-danger px-50 py-25 rounded">
                      <feather-icon
icon="TrendingDownIcon"
size="12"
/> -2%
                    </span>
                  </div>
                  <vue-apex-charts
type="area"
height="60"
:options="sparklineOptionsDanger"
:series="sparklineSupplies"
/>
                </b-card>
              </b-col>

              <b-col
sm="6"
class="animate-slide-up"
style="animation-delay: 0.25s;"
>
                <b-card
no-body
class="modern-card p-2 h-100"
>
                  <div class="d-flex justify-content-between mb-50">
                    <span class="text-muted font-weight-medium small">Finished MR</span>
                    <span class="font-weight-bolder text-dark">{{ finishedMR }}</span>
                  </div>
                  <b-progress
:value="finishedMR"
max="500"
height="6px"
variant="primary"
class="rounded-pill mb-50"
/>
                  <span
class="small text-muted"
style="font-size: 0.7rem;"
>Total completed requests</span>
                </b-card>
              </b-col>

              <b-col
sm="6"
class="animate-slide-up"
style="animation-delay: 0.3s;"
>
                <b-card
no-body
class="modern-card p-2 h-100"
>
                  <div class="d-flex justify-content-between mb-50">
                    <span class="text-muted font-weight-medium small">Total PR Plan</span>
                    <span class="font-weight-bolder text-dark">{{ prPlan }}</span>
                  </div>
                  <b-progress
:value="prPlan"
max="1000"
height="6px"
variant="warning"
class="rounded-pill mb-50"
/>
                  <span
class="small text-muted"
style="font-size: 0.7rem;"
>Planned vs Threshold</span>
                </b-card>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-col>

      <b-col
md="6"
cols="12"
class="animate-slide-up"
style="animation-delay: 0.4s;"
>
        <b-card
title="Branch Cumulative Distribution"
class="modern-card mb-4 h-100"
title-tag="h4"
>
          <b-card-body class="p-0 pt-2">
            <b-skeleton
v-if="loadingCharts"
height="350px"
width="100%"
class="rounded"
/>
            <vue-apex-charts
v-else-if="series.length > 0"
type="donut"
height="350"
:options="wideChartOptions"
:series="series"
class="chart-container"
/>
            <div
v-else
class="loading-skeleton d-flex flex-column align-items-center justify-content-center py-5 rounded"
>
              <b-spinner
small
variant="primary"
class="mb-2"
/>
              <span class="text-muted font-weight-medium">Loading branch data...</span>
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col
md="6"
cols="12"
class="animate-slide-up"
style="animation-delay: 0.5s;"
>
        <b-card
title="Total Data This Year"
class="modern-card mb-4 h-100"
title-tag="h4"
>
          <b-skeleton
v-if="loadingCharts"
height="350px"
width="100%"
class="rounded"
/>
          <vue-apex-charts
v-else-if="seriesThisYear.length > 0"
type="donut"
height="350"
:options="wideChartOptionsYearly"
:series="seriesThisYear"
class="chart-container"
/>
          <div
v-else
class="empty-state text-center py-5 rounded mt-2"
>
            <feather-icon
icon="CalendarIcon"
size="48"
class="text-light mb-2 opacity-50"
/>
            <h5 class="text-dark font-weight-bold">
No activities recorded yet
</h5>
            <p class="small text-muted mb-0">
New data will appear once the first PO is created.
</p>
          </div>
        </b-card>
      </b-col>

      <b-col
cols="12"
class="animate-slide-up"
style="animation-delay: 0.6s;"
>
        <b-card
no-body
class="modern-card mb-4"
>
          <b-card-header class="border-bottom-0 pt-3 pb-2">
            <b-card-title class="font-weight-bolder">
Critical Stock Levels
</b-card-title>
          </b-card-header>
          <div class="table-responsive interactive-table">
            <dashboard-table
              :data="dataTable"
              :loading="loadingTable"
            />
          </div>
        </b-card>
      </b-col>

    </b-row>
  </section>
</template>

<script>
import {
  BRow, BCol, BCard, BCardHeader, BCardTitle, BCardBody, BSpinner, BProgress, BSkeleton,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ShimmerCardGrid from '@core/components/skeleton/ShimmerCardGrid.vue'
import axios from 'axios'
import { getUserData } from '@/auth/utils'
import apiEndpoints from '@/constants/apiEndpoints'
import DashboardTable from './DashboardTable.vue'

export default {
  components: {
    BCard,
BCardHeader,
BCardTitle,
BCardBody,
BRow,
BCol,
BSpinner,
BProgress,
BSkeleton,
    DashboardTable,
VueApexCharts,
    ShimmerCardGrid,
  },
  data() {
    // Shared Base Options for Main Donut Charts
    const baseDonutOptions = {
      chart: {
        toolbar: { show: false },
        fontFamily: "'Inter', sans-serif",
        animations: { enabled: true, easing: 'easeinout', speed: 800 },
      },
      // Removed backend colors to let Apex generate a clean palette for the Top 5
      theme: { palette: 'palette1' },
      tooltip: { theme: 'light', style: { fontSize: '14px', fontFamily: "'Inter', sans-serif" } },
      plotOptions: {
        pie: {
          donut: {
            size: '72%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total Volume',
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                formatter: val => val.globals.seriesTotals.reduce((a, b) => a + b, 0),
              },
            },
          },
        },
      },
      legend: { position: 'bottom', offsetY: 0 },
      dataLabels: { enabled: false },
    }

    return {
      information: '',
      now: new Date(),
      clockTimer: null,
      firstName: 'User',
      loadingStats: true,
      loadingTable: true,
      loadingCharts: true,
      exchangeLoading: false,
      exchangeUpdatedAt: null,
      // true = show "1 [FX] -> Rp [amount]" (default), false = "1 IDR -> [FX] [amount]"
      quoteInIdr: true,
      exchangeRateHistory: {},
      exchangeRates: {
        USD: 0.000061,
        SGD: 0.000082,
        CNY: 0.00044,
        EUR: 0.000056,
        GBP: 0.000048,
      },
      exchangeOptions: [
        {
          code: 'USD', label: 'US Dollar', locale: 'en-US', flag: 'US', color: '#2b8a7e',
        },
        {
          code: 'EUR', label: 'Euro', locale: 'de-DE', flag: 'EU', color: '#3b82f6',
        },
        {
          code: 'CNY', label: 'Chinese Yuan', locale: 'zh-CN', flag: 'CN', color: '#ea5455',
        },
        {
          code: 'GBP', label: 'Pound Sterling', locale: 'en-GB', flag: 'GB', color: '#8b5cf6',
        },
        {
          code: 'SGD', label: 'Singapore Dollar', locale: 'en-SG', flag: 'SG', color: '#f97316',
        },
      ],

      // REAL DATA STATES
      pendingMR: 0,
finishedMR: 0,
pendingPRLocal: 0,
pendingPRSupplies: 0,
      pendingPRHO: 0,
pendingPOLocal: 0,
pendingPOHO: 0,
prPlan: 0,

      dataTable: [],
      series: [],
      seriesThisYear: [],

      // TREND STATES
      sparklineMR: [{ name: 'MR Volume', data: [12, 14, 18, 17, 21, 19, 25] }],
      sparklineSupplies: [{ name: 'Supplies', data: [25, 22, 19, 15, 14, 10, 8] }],

      wideChartOptions: { ...baseDonutOptions },
      wideChartOptionsYearly: { ...baseDonutOptions },

      sparklineOptions: {
        chart: { type: 'area', sparkline: { enabled: true } },
        stroke: { curve: 'smooth', width: 2 },
        colors: ['#2b8a7e'],
        fill: {
 type: 'gradient',
gradient: {
 shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05, stops: [0, 100],
},
},
        tooltip: { fixed: { enabled: false }, x: { show: false }, marker: { show: false } },
      },
      sparklineOptionsDanger: {
        chart: { type: 'area', sparkline: { enabled: true } },
        stroke: { curve: 'smooth', width: 2 },
        colors: ['#ea5455'],
        fill: {
 type: 'gradient',
gradient: {
 shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05, stops: [0, 100],
},
},
        tooltip: { fixed: { enabled: false }, x: { show: false }, marker: { show: false } },
      },
    }
  },
  computed: {
    currentHour() {
      return this.now.getHours()
    },
    currentTime() {
      return this.now.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },
    formattedDate() {
      return this.now.toLocaleDateString([], {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    },
    timezoneLabel() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local time'
    },
    greetingText() {
      if (this.currentHour >= 5 && this.currentHour < 12) return 'Good morning'
      if (this.currentHour >= 12 && this.currentHour < 17) return 'Good afternoon'
      if (this.currentHour >= 17 && this.currentHour < 20) return 'Good evening'
      return 'Good night'
    },
    greetingMessage() {
      if (this.currentHour >= 5 && this.currentHour < 12) return 'A clear start for procurement decisions, approvals, and branch movement.'
      if (this.currentHour >= 12 && this.currentHour < 17) return 'Keep the operation moving with live procurement visibility and focused follow-through.'
      if (this.currentHour >= 17 && this.currentHour < 20) return 'The day is closing. Review what needs attention before handover.'
      return 'Night mode is active. Critical procurement signals stay visible while the operation quiets down.'
    },
    timeSceneClass() {
      if (this.currentHour >= 5 && this.currentHour < 12) return 'scene-morning'
      if (this.currentHour >= 12 && this.currentHour < 17) return 'scene-day'
      if (this.currentHour >= 17 && this.currentHour < 20) return 'scene-evening'
      return 'scene-night'
    },
    isNightScene() {
      return this.currentHour >= 20 || this.currentHour < 5
    },
    exchangeUpdatedLabel() {
      if (!this.exchangeUpdatedAt) return 'just now (indicative)'
      return this.exchangeUpdatedAt
    },
    exchangeInitialLoading() {
      return this.exchangeLoading && !this.exchangeUpdatedAt
    },
  },
  mounted() {
    this.firstName = this.getFirstName()
    this.clockTimer = window.setInterval(() => {
      this.now = new Date()
    }, 1000)
    this.welcomeUser()
    this.initDashboard()
    this.loadRateHistory()
    this.fetchExchangeRates()
  },
  beforeDestroy() {
    if (this.clockTimer) window.clearInterval(this.clockTimer)
  },
  methods: {
    getFirstName() {
      const userData = getUserData()
      return userData?.user_fullname ? userData.user_fullname.split(' ')[0] : 'User'
    },
    welcomeUser() {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: `Hi, ${this.firstName}`,
          icon: 'ActivityIcon',
          variant: 'success',
          text: 'Live data successfully synced with backend.',
        },
      })
    },
    // Raw rate is always "1 IDR expressed in `code`" (e.g. USD: 0.000061).
    // Displayed value flips depending on `quoteInIdr`.
    valueFor(code, rawRate) {
      const raw = Number(rawRate)
      if (!raw) return 0
      return this.quoteInIdr ? 1 / raw : raw
    },
    formattedRate(code) {
      const raw = this.exchangeRates[code]
      const value = this.valueFor(code, raw)
      const option = this.exchangeOptions.find(o => o.code === code) || {}

      if (this.quoteInIdr) {
        return new Intl.NumberFormat('id-ID', {
          style: 'currency',
          currency: 'IDR',
          maximumFractionDigits: 0,
        }).format(value)
      }

      return new Intl.NumberFormat(option.locale || 'en-US', {
        style: 'currency',
        currency: code,
        maximumFractionDigits: 4,
      }).format(value)
    },
    historyValues(code) {
      const history = this.exchangeRateHistory[code] || []
      return history.map(point => this.valueFor(code, point.rate))
    },
    pctChange(code) {
      const values = this.historyValues(code)
      if (values.length < 2) return 0

      const first = values[0]
      const last = values[values.length - 1]
      if (!first) return 0

      return ((last - first) / first) * 100
    },
    trendLabel(code) {
      const count = (this.exchangeRateHistory[code] || []).length
      if (count < 2) return 'building trend'
      return `${count}d`
    },
    sparklinePoints(code) {
      const values = this.historyValues(code)
      if (values.length < 2) return null

      const min = Math.min(...values)
      const max = Math.max(...values)
      const span = max - min || 1
      const stepX = 100 / (values.length - 1)

      return values
        .map((value, index) => {
          const x = index * stepX
          const y = 28 - ((value - min) / span) * 24
          return `${x.toFixed(1)},${y.toFixed(1)}`
        })
        .join(' ')
    },
    lastPoint(code) {
      const points = this.sparklinePoints(code)
      if (!points) return null

      const last = points.split(' ').pop()
      const [x, y] = last.split(',').map(Number)
      return { x, y }
    },
    loadRateHistory() {
      try {
        const raw = window.localStorage.getItem('geoproc_fx_history')
        this.exchangeRateHistory = raw ? JSON.parse(raw) : {}
      } catch (error) {
        this.exchangeRateHistory = {}
      }
    },
    recordRateHistory(rates) {
      const today = new Date().toISOString().slice(0, 10)
      const nextHistory = { ...this.exchangeRateHistory }

      Object.entries(rates).forEach(([code, rate]) => {
        const points = nextHistory[code] ? [...nextHistory[code]] : []
        const last = points[points.length - 1]

        if (last && last.day === today) {
          last.rate = rate
        } else {
          points.push({ day: today, rate })
        }

        nextHistory[code] = points.slice(-30)
      })

      this.exchangeRateHistory = nextHistory
      try {
        window.localStorage.setItem('geoproc_fx_history', JSON.stringify(nextHistory))
      } catch (error) {
        // localStorage unavailable (e.g. private browsing) — safe to ignore
      }
    },
    async fetchExchangeRates() {
      this.exchangeLoading = true
      try {
        const { data } = await axios.get('https://open.er-api.com/v6/latest/IDR', { timeout: 7000 })
        const rates = data?.rates || {}
        const nextRates = { ...this.exchangeRates }

        this.exchangeOptions.forEach(option => {
          if (rates[option.code]) nextRates[option.code] = Number(rates[option.code])
        })

        this.exchangeRates = nextRates
        this.recordRateHistory(nextRates)
        const updatedAt = data?.time_last_update_utc ? new Date(data.time_last_update_utc) : new Date()
        this.exchangeUpdatedAt = updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      } catch (error) {
        this.exchangeUpdatedAt = null
      } finally {
        this.exchangeLoading = false
      }
    },

    // --- NEW HELPER METHOD TO PACK DATA ---
    formatChartData(rawSeries, rawLabels, topN = 5) {
      // 1. Combine arrays into objects and filter out zeroes
      const combined = rawSeries
        .map((value, index) => ({ value, label: rawLabels[index] }))
        .filter(item => item.value > 0)

      // 2. Sort from highest to lowest
      combined.sort((a, b) => b.value - a.value)

      // 3. Slice the Top N branches
      const topItems = combined.slice(0, topN)
      const otherItems = combined.slice(topN)

      // 4. Group the rest into "Others"
      if (otherItems.length > 0) {
        const othersSum = otherItems.reduce((sum, item) => sum + item.value, 0)
        topItems.push({ label: 'Others', value: othersSum })
      }

      // 5. Unpack back into arrays for ApexCharts
      return {
        series: topItems.map(item => item.value),
        labels: topItems.map(item => item.label),
      }
    },

    async initDashboard() {
      const headers = {
        Authorization: `Bearer ${this.$cookies.get('userToken')}`,
        'Content-Type': 'application/json',
      }
      try {
        const [dash, stats, statsYear] = await Promise.all([
          axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_DASHBOARD}`, { headers }),
          axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PO_SUMMARY_PIE}`, { headers }),
          this.fetchYearlyStats(headers),
        ])

        if (dash.data.success) {
          const d = dash.data.data

          this.pendingMR = d.pendingMR || 0
          this.finishedMR = d.finishedMR || 0
          this.pendingPRLocal = d.pendingPRLocal || 0
          this.pendingPRSupplies = d.pendingPRSupplies || 0
          this.pendingPRHO = d.pendingPRHO || 0
          this.pendingPOLocal = d.pendingPOLocal || 0
          this.pendingPOHO = d.pendingPOHO || 0
          this.prPlan = d.prPlan || 0

          if (d.itemStock && d.itemStock.length > 0) {
            const header = ['No', 'Item', 'Code', 'Stock', 'Min']
            const rows = d.itemStock.map((o, i) => [
              i + 1, o.item_name, o.item_code, o.item_stock_current_available, o.item_min_stock_number,
            ])
            this.dataTable = [header, ...rows]
          }
        }

        // --- APPLYING THE CHART FORMATTER ---
        if (stats.data.success) {
          const formatted = this.formatChartData(
            stats.data.data.series,
            stats.data.data.chartOptions.labels,
            5, // Change this number if you want Top 3 or Top 10
          )
          this.series = formatted.series
          this.wideChartOptions = { ...this.wideChartOptions, labels: formatted.labels }
        }

        if (statsYear.data.success) {
          const formattedYearly = this.formatChartData(
            statsYear.data.data.series,
            statsYear.data.data.chartOptions.labels,
            5, // Change this number if you want Top 3 or Top 10
          )
          this.seriesThisYear = formattedYearly.series
          this.wideChartOptionsYearly = { ...this.wideChartOptionsYearly, labels: formattedYearly.labels }
        }
      } catch (err) {
        if (err.response?.status === 401) this.$router.push({ name: 'auth-login' })
      } finally {
        this.loadingStats = false
        this.loadingTable = false
        this.loadingCharts = false
      }
    },
    fetchYearlyStats(headers) {
      const year = new Date().getFullYear()
      const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PO_SUMMARY_PIE}?date_start=${year}-01-01&date_end=${year}-12-31`
      return axios.get(url, { headers })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/pages/dashboard.scss';
@import '@core/scss/vue/libs/chart-apex.scss';
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/* --- Master Theme --- */
.modern-dashboard {
  font-family: 'Inter', sans-serif;
  color: #1f2937;
}

.greeting-panel {
  position: relative;
  overflow: hidden;
  min-height: 240px;
  margin-bottom: 1.5rem;
  border: 0;
  border-radius: 12px;
  color: #fff;
  box-shadow: 0 18px 45px rgba(28, 35, 53, 0.16) !important;
}

.greeting-panel__content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  min-height: 240px;
  padding: 2rem;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background:
      linear-gradient(90deg, rgba(8, 14, 28, 0.62) 0%, rgba(8, 14, 28, 0.32) 55%, rgba(8, 14, 28, 0.04) 100%),
      linear-gradient(0deg, rgba(0, 0, 0, 0.12), rgba(255, 255, 255, 0));
  }
}

.greeting-copy {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 620px;

  h1 {
    margin-bottom: 0.75rem;
    color: #fff;
    font-size: 2.25rem;
    font-weight: 700;
    letter-spacing: 0;
    text-shadow: 0 1px 3px rgba(10, 16, 30, 0.55), 0 4px 16px rgba(10, 16, 30, 0.4);
  }

  p {
    max-width: 560px;
    margin-bottom: 1.25rem;
    color: #fff;
    font-size: 1rem;
    line-height: 1.65;
    text-shadow: 0 1px 3px rgba(10, 16, 30, 0.5), 0 2px 10px rgba(10, 16, 30, 0.35);
  }
}

.greeting-kicker {
  display: inline-block;
  margin-bottom: 0.75rem;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-shadow: 0 1px 3px rgba(10, 16, 30, 0.5), 0 2px 10px rgba(10, 16, 30, 0.35);
}

.greeting-clock {
  display: inline-flex;
  align-items: baseline;
  align-self: flex-start;
  padding: 0.7rem 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.16);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(8px);

  span {
    font-family: 'Inter', sans-serif;
    font-size: 1.05rem;
    font-weight: 700;
    color: #fff;
  }

  small {
    margin-left: 0.75rem;
    color: #fff;
    font-size: 0.78rem;
    opacity: 0.92;
  }
}

.sky-stage {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

.scene-morning {
  background:
    radial-gradient(circle at 74% 38%, rgba(255, 219, 158, 0.42) 0, rgba(255, 219, 158, 0) 30%),
    linear-gradient(135deg, #235a8c 0%, #4f97c4 45%, #f0b878 100%);
}

.scene-day {
  background:
    radial-gradient(circle at 76% 34%, rgba(255, 244, 210, 0.4) 0, rgba(255, 244, 210, 0) 32%),
    linear-gradient(135deg, #255f85 0%, #2b8a7e 52%, #a9d8cd 100%);
}

.scene-evening {
  background:
    radial-gradient(circle at 76% 38%, rgba(255, 190, 130, 0.36) 0, rgba(255, 190, 130, 0) 32%),
    linear-gradient(135deg, #2c3e6b 0%, #5a7fa8 42%, #e2946a 100%);
}

.scene-night {
  background:
    radial-gradient(circle at 78% 32%, rgba(178, 200, 255, 0.26) 0, rgba(178, 200, 255, 0) 30%),
    linear-gradient(135deg, #1f3f63 0%, #2c5a82 50%, #3f7595 100%);
}

.day-sky,
.night-sky {
  position: absolute;
  inset: 0;
}

.sun-rays {
  position: absolute;
  top: -45px;
  right: 27px;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  background: repeating-conic-gradient(
    from 0deg,
    rgba(255, 226, 150, 0.65) 0deg 3deg,
    rgba(255, 226, 150, 0) 3deg 20deg
  );
  -webkit-mask-image: radial-gradient(circle, rgba(0, 0, 0, 0) 26%, rgba(0, 0, 0, 0.9) 40%, rgba(0, 0, 0, 0) 74%);
  mask-image: radial-gradient(circle, rgba(0, 0, 0, 0) 26%, rgba(0, 0, 0, 0.9) 40%, rgba(0, 0, 0, 0) 74%);
  opacity: 0.85;
  animation: sunRaysSpin 70s linear infinite;
  pointer-events: none;
}

.sun-core {
  position: absolute;
  top: 34px;
  right: 106px;
  width: 102px;
  height: 102px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 36% 34%, #fff9dd 0%, #fff2b4 24%, #ffc260 58%, #f58b3c 100%);
  box-shadow:
    0 0 28px rgba(255, 238, 177, 0.72),
    0 0 86px rgba(255, 168, 76, 0.5),
    0 0 160px rgba(255, 201, 116, 0.34);
  animation: sunBreathe 8s ease-in-out infinite;
}

.sun-halo {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(255, 244, 196, 0.2);
  animation: haloPulse 9s ease-in-out infinite;
}

.sun-halo-1 {
  top: 6px;
  right: 78px;
  width: 158px;
  height: 158px;
}

.sun-halo-2 {
  top: -28px;
  right: 44px;
  width: 226px;
  height: 226px;
  animation-delay: 1.2s;
}

.cloud {
  position: absolute;
  height: 58px;
  border-radius: 999px;
  background:
    radial-gradient(ellipse at 22% 58%, rgba(255, 255, 255, 0.92) 0 24%, rgba(255, 255, 255, 0) 55%),
    radial-gradient(ellipse at 48% 38%, rgba(255, 255, 255, 0.88) 0 30%, rgba(255, 255, 255, 0) 63%),
    radial-gradient(ellipse at 73% 57%, rgba(247, 252, 255, 0.78) 0 27%, rgba(247, 252, 255, 0) 62%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.68), rgba(226, 238, 247, 0.18));
  filter: blur(2.4px) saturate(1.04);
  box-shadow:
    32px 10px 38px rgba(255, 255, 255, 0.24),
    -24px 18px 34px rgba(177, 204, 223, 0.14);
  opacity: 0.72;
}

.cloud::before,
.cloud::after {
  content: '';
  position: absolute;
  border-radius: 999px;
  background:
    radial-gradient(ellipse at 35% 48%, rgba(255, 255, 255, 0.72) 0 32%, rgba(255, 255, 255, 0) 68%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.28), rgba(220, 235, 246, 0.08));
  filter: blur(5px);
}

.cloud::before {
  top: -28px;
  left: 28px;
  width: 132px;
  height: 78px;
}

.cloud::after {
  top: -18px;
  right: 22px;
  width: 116px;
  height: 70px;
}

.cloud-1 {
  right: 38px;
  bottom: 44px;
  width: 300px;
  animation: cloudDrift 42s ease-in-out infinite;
}

.cloud-2 {
  right: 224px;
  top: 58px;
  width: 230px;
  opacity: 0.42;
  animation: cloudDriftSmall 54s ease-in-out infinite;
}

.cloud-3 {
  right: -38px;
  top: 132px;
  width: 260px;
  opacity: 0.28;
  animation: cloudDriftFar 68s ease-in-out infinite reverse;
}

.light-streak {
  position: absolute;
  right: 0;
  width: 360px;
  height: 1px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 246, 215, 0.56), rgba(255, 255, 255, 0));
  transform: rotate(-18deg);
  opacity: 0.5;
  animation: lightSweep 7s ease-in-out infinite;
}

.light-streak-1 {
  top: 92px;
}

.light-streak-2 {
  top: 148px;
  right: 66px;
  animation-delay: 1.8s;
}

.moon {
  position: absolute;
  top: 38px;
  right: 118px;
  width: 92px;
  height: 92px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 34% 30%, #f7fbff 0%, #dbe6f7 42%, #9daecb 100%);
  box-shadow:
    inset -14px -10px 20px rgba(81, 97, 125, 0.32),
    0 0 36px rgba(218, 231, 255, 0.46),
    0 0 110px rgba(122, 157, 221, 0.28);
  animation: moonFloat 9s ease-in-out infinite;
}

.moon::after {
  content: '';
  position: absolute;
  top: 18px;
  left: 23px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(112, 128, 156, 0.22);
  box-shadow:
    27px 12px 0 rgba(112, 128, 156, 0.16),
    18px 40px 0 rgba(112, 128, 156, 0.12);
}

.moon-glow {
  position: absolute;
  top: -14px;
  right: 66px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(191, 213, 255, 0.2), rgba(191, 213, 255, 0));
  animation: haloPulse 10s ease-in-out infinite;
}

.star {
  position: absolute;
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.72);
  animation: starTwinkle 4s ease-in-out infinite;
}

.star-1 { top: 36px; right: 328px; animation-delay: 0.2s; }
.star-2 { top: 82px; right: 420px; animation-delay: 1.1s; }
.star-3 { top: 154px; right: 290px; animation-delay: 2s; }
.star-4 { top: 118px; right: 86px; animation-delay: 0.8s; }
.star-5 { top: 42px; right: 38px; animation-delay: 2.6s; }

.night-haze {
  position: absolute;
  height: 42px;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(173, 190, 224, 0.2), rgba(91, 118, 164, 0.04));
  filter: blur(8px);
  opacity: 0.5;
  animation: cloudDriftSmall 26s ease-in-out infinite;
}

.night-haze-1 {
  right: 48px;
  bottom: 44px;
  width: 300px;
}

.night-haze-2 {
  right: 260px;
  top: 80px;
  width: 210px;
  opacity: 0.32;
  animation-delay: 2s;
}

@keyframes sunBreathe {
  0%, 100% { transform: scale(1); filter: brightness(1); }
  50% { transform: scale(1.04); filter: brightness(1.08); }
}

@keyframes sunRaysSpin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes haloPulse {
  0%, 100% { transform: scale(1); opacity: 0.55; }
  50% { transform: scale(1.08); opacity: 0.28; }
}

@keyframes cloudDrift {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(-42px, -4px, 0) scale(1.03); }
}

@keyframes cloudDriftSmall {
  0%, 100% { transform: translate3d(0, 0, 0) scale(0.78); }
  50% { transform: translate3d(36px, 5px, 0) scale(0.8); }
}

@keyframes cloudDriftFar {
  0%, 100% { transform: translate3d(0, 0, 0) scale(0.62); }
  50% { transform: translate3d(-34px, 6px, 0) scale(0.64); }
}

@keyframes lightSweep {
  0%, 100% { opacity: 0.2; transform: translateX(24px) rotate(-18deg); }
  50% { opacity: 0.62; transform: translateX(-18px) rotate(-18deg); }
}

@keyframes moonFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(8px); }
}

@keyframes starTwinkle {
  0%, 100% { opacity: 0.35; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.9); }
}

/* --- Base Card Styling --- */
.modern-card {
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
  background-color: #ffffff;
  transition: all 0.2s ease-in-out;
}
.border-left-primary { border-left: 4px solid #2b8a7e !important; }
.header-title { font-size: 1.15rem; letter-spacing: -0.01em; }

.exchange-widget {
  overflow: hidden;
  border: 1px solid rgba(32, 36, 54, 0.08) !important;
  border-radius: 12px;
  background:
    radial-gradient(circle at 94% 8%, rgba(43, 138, 126, 0.15), transparent 28%),
    linear-gradient(135deg, #ffffff 0%, #f8fbfd 100%);
  box-shadow: 0 16px 38px rgba(32, 36, 54, 0.08) !important;
}

.exchange-widget__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.15rem 1.25rem;
  border-bottom: 1px solid rgba(32, 36, 54, 0.06);
}

.exchange-widget__title {
  display: flex;
  align-items: center;
  gap: 0.7rem;

  h3 {
    margin: 0;
    color: #202436;
    font-size: 1.05rem;
    font-weight: 850;
  }

  small {
    display: block;
    margin-top: 0.1rem;
    color: #8892a3;
    font-size: 0.72rem;
    font-weight: 650;
  }
}

.exchange-widget__icon {
  display: grid;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  place-items: center;
  color: #2b8a7e;
  border-radius: 50%;
  background: rgba(43, 138, 126, 0.12);
}

.exchange-widget__refresh {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  color: #8892a3;
  border: 0;
  border-radius: 50%;
  background: transparent;
  transition: all 0.18s ease;

  &:hover:not(:disabled) {
    color: #fff;
    background: linear-gradient(135deg, #2b8a7e, #255f85);
    transform: rotate(90deg);
  }
}

.exchange-rates-list {
  padding: 0.35rem 0.25rem;
}

.exchange-rate-row {
  display: grid;
  grid-template-columns: 40px 1fr 90px auto;
  align-items: center;
  gap: 0.9rem;
  padding: 0.65rem 1rem;
  border-radius: 10px;
  transition: background-color 0.16s ease;

  &:hover {
    background: rgba(43, 138, 126, 0.05);
  }
}

.exchange-rate-row__flag {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  border-radius: 50%;
  font-size: 0.72rem;
  font-weight: 800;
}

.exchange-rate-row__info {
  min-width: 0;

  strong {
    display: block;
    overflow: hidden;
    color: #202436;
    font-size: 0.9rem;
    font-weight: 800;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.exchange-rate-row__change {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  margin-top: 0.15rem;
  color: #28c76f;
  font-size: 0.74rem;
  font-weight: 750;

  small {
    color: #8892a3;
    font-weight: 650;
  }

  &.is-down {
    color: #ea5455;
  }
}

.exchange-rate-row__spark {
  width: 90px;
  height: 32px;
  display: block;
}

.exchange-rate-row__spark--empty {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 1px;
    background: repeating-linear-gradient(90deg, rgba(32, 36, 54, 0.14) 0 4px, transparent 4px 8px);
  }
}

.exchange-rate-row__value {
  color: #202436;
  font-size: 0.92rem;
  font-weight: 850;
  text-align: right;
  white-space: nowrap;
}

.exchange-widget__foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem 0.9rem;
  padding: 0.8rem 1.25rem;
  border-top: 1px solid rgba(32, 36, 54, 0.06);
  color: #8892a3;
  font-size: 0.7rem;
  font-weight: 650;
}

.exchange-widget__flip {
  margin-left: auto;
  padding: 0.3rem 0.7rem;
  color: #1d6f66;
  border: 1px solid rgba(43, 138, 126, 0.24);
  border-radius: 999px;
  background: rgba(43, 138, 126, 0.08);
  font-size: 0.7rem;
  font-weight: 750;
  transition: all 0.18s ease;

  &:hover {
    color: #fff;
    background: linear-gradient(135deg, #2b8a7e, #255f85);
    border-color: transparent;
  }
}

::v-deep body.dark-layout,
::v-deep .dark-layout {
  .modern-dashboard {
    color: #f4f7fb;
  }
}

body.dark-layout .modern-dashboard,
.dark-layout .modern-dashboard {
  .modern-card,
  .exchange-widget {
    border-color: rgba(255, 255, 255, 0.1) !important;
    background:
      radial-gradient(circle at 94% 8%, rgba(73, 184, 171, 0.12), transparent 28%),
      linear-gradient(135deg, #161d31 0%, #111827 100%) !important;
    box-shadow: 0 16px 38px rgba(0, 0, 0, 0.24) !important;
  }

  .text-dark,
  .exchange-widget__head h3,
  .exchange-rate-row__info strong,
  .exchange-rate-row__value {
    color: #f4f7fb !important;
  }

  .text-muted,
  .exchange-widget__title small,
  .exchange-rate-row__change small {
    color: #a7b0c3 !important;
  }

  .exchange-widget__head,
  .exchange-widget__foot {
    border-color: rgba(255, 255, 255, 0.08);
  }

  .exchange-rate-row:hover,
  .output-node,
  .node-circle {
    border-color: rgba(255, 255, 255, 0.1) !important;
    background: #111827 !important;
    color: #f4f7fb !important;
  }

  .exchange-widget__flip {
    color: #86e7dc;
    border-color: rgba(73, 184, 171, 0.32);
    background: rgba(73, 184, 171, 0.1);

    &:hover {
      color: #fff;
      background: linear-gradient(135deg, #49b8ab, #2f739d);
    }
  }

  .workflow-line {
    border-top-color: rgba(255, 255, 255, 0.14);
  }
}

/* --- Interactive Elements --- */
::v-deep .interactive-table table tbody tr { transition: background-color 0.2s ease-in-out; }
::v-deep .interactive-table table tbody tr:hover { background-color: #f8f9fa !important; }
.loading-skeleton, .empty-state { background-color: #f9fafb; border: 1px dashed rgba(0, 0, 0, 0.1); }

/* --- Badges --- */
.bg-light-success { background-color: rgba(40, 199, 111, 0.12); }
.bg-light-danger { background-color: rgba(234, 84, 85, 0.12); }

/* --- Workflow Pipeline Visualization --- */
.workflow-line {
  position: absolute;
  width: 100%;
  border-top: 3px dashed rgba(0, 0, 0, 0.1);
  top: 35%;
  z-index: 0;
}
.workflow-node { position: relative; z-index: 1; }
.node-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #ffffff;
  border: 4px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:hover {
    transform: scale(1.05) translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08) !important;
  }
}
.node-circle-lg { width: 76px; height: 76px; border-width: 5px; }
.output-node {
  min-width: 140px;
  border: 1px solid rgba(0,0,0,0.06) !important;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06) !important;
  }
}

/* --- Entrance Animations ---
   IMPORTANT: this must never hide content via a static `opacity: 0` that
   depends on the animation actually firing. iOS Safari has a well-known bug
   where delayed CSS animations (especially many staggered ones on route
   entry, as here) sometimes never paint past their 0% keyframe until a
   forced repaint happens — e.g. the user scrolls. That produces exactly a
   "content invisible until you scroll" symptom, which is what this looked
   like. Content now stays visible (opacity: 1) the whole time; only the
   subtle translateY slide is animated, so worst case if the animation never
   plays, the block simply appears in its resting position with no slide —
   never invisible. */
.animate-slide-up {
  animation: slideUpFade 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes slideUpFade {
  0% { transform: translateY(20px); }
  100% { transform: translateY(0); }
}

/* --- Responsive Adjustments --- */
@media (max-width: 1199.98px) {
  .greeting-panel {
    min-height: 230px;
  }

  .greeting-panel__content {
    min-height: 230px;
    padding: 1.65rem;
  }

  .greeting-copy {
    max-width: 560px;

    h1 {
      font-size: 2rem;
    }

    p {
      max-width: 500px;
      font-size: 0.95rem;
    }
  }

  .sun-rays {
    top: -28px;
    right: 6px;
    width: 220px;
    height: 220px;
  }

  .sun-core {
    top: 38px;
    right: 72px;
    width: 88px;
    height: 88px;
  }

  .sun-halo-1 {
    top: 12px;
    right: 48px;
    width: 138px;
    height: 138px;
  }

  .sun-halo-2 {
    top: -16px;
    right: 18px;
    width: 196px;
    height: 196px;
  }

  .cloud-1 {
    right: -8px;
    bottom: 38px;
    width: 258px;
  }

  .cloud-2 {
    right: 132px;
    top: 72px;
    width: 198px;
  }

  .moon {
    right: 74px;
    width: 82px;
    height: 82px;
  }

  .moon-glow {
    right: 26px;
  }
}

@media (max-width: 768px) {
  .exchange-rate-row {
    grid-template-columns: 34px 1fr auto;
    gap: 0.6rem;
  }

  .exchange-rate-row__spark {
    display: none;
  }

  .exchange-rate-row__flag {
    width: 34px;
    height: 34px;
  }

  .greeting-panel {
    min-height: 190px;
    border-radius: 10px;
  }

  .greeting-panel__content {
    min-height: 190px;
    padding: 1.1rem;
  }

  .greeting-copy {
    justify-content: center;
    max-width: 100%;

    h1 {
      max-width: 90%;
      margin-bottom: 0.4rem;
      font-size: 1.28rem;
      line-height: 1.2;
    }

    p {
      display: none;
    }
  }

  .greeting-kicker {
    margin-bottom: 0.4rem;
    font-size: 0.66rem;
  }

  .greeting-clock {
    max-width: 100%;
    padding: 0.4rem 0.6rem;

    span {
      font-size: 0.86rem;
    }

    small {
      margin-top: 0;
      font-size: 0.68rem;
    }
  }

  .greeting-panel::after {
    background:
      linear-gradient(90deg, rgba(10, 20, 38, 0.68) 0%, rgba(10, 20, 38, 0.42) 62%, rgba(10, 20, 38, 0.08) 100%),
      linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(255, 255, 255, 0));
  }

  .sun-rays {
    top: -27px;
    right: -23px;
    width: 168px;
    height: 168px;
  }

  .sun-core {
    top: 24px;
    right: 28px;
    width: 66px;
    height: 66px;
  }

  .sun-halo-1 {
    top: 2px;
    right: 6px;
    width: 112px;
    height: 112px;
  }

  .sun-halo-2 {
    top: -26px;
    right: -22px;
    width: 166px;
    height: 166px;
  }

  .cloud {
    height: 44px;
    filter: blur(3px) saturate(1.02);
  }

  .cloud-1 {
    right: -86px;
    bottom: 28px;
    width: 238px;
    opacity: 0.58;
  }

  .cloud-2 {
    right: 22px;
    top: 132px;
    width: 190px;
    opacity: 0.34;
  }

  .cloud-3 {
    right: -88px;
    top: 92px;
    width: 208px;
    opacity: 0.24;
  }

  .light-streak {
    width: 220px;
    opacity: 0.32;
  }

  .moon {
    top: 26px;
    right: 34px;
    width: 66px;
    height: 66px;
  }

  .moon-glow {
    top: -20px;
    right: -8px;
    width: 148px;
    height: 148px;
  }

  .star-1 { right: 166px; }
  .star-2 { right: 238px; }
  .star-3 { right: 142px; }
  .star-4 { right: 38px; }
  .star-5 { right: 18px; }

  .night-haze-1 {
    right: -80px;
    width: 220px;
  }

  .night-haze-2 {
    right: 80px;
    top: 132px;
    width: 160px;
  }

  .workflow-line { top: 25%; }
  .node-circle { width: 48px; height: 48px; border-width: 3px; }
  .node-circle-lg { width: 58px; height: 58px; }
  .output-node { min-width: 120px; }
}

@media (max-width: 420px) {
  .greeting-panel {
    min-height: 170px;
  }

  .greeting-panel__content {
    min-height: 170px;
    padding: 0.95rem;
  }

  .greeting-copy {
    h1 {
      max-width: 100%;
      font-size: 1.16rem;
    }
  }

  .sun-core,
  .sun-rays,
  .moon {
    opacity: 0.78;
  }

  .cloud-1 {
    bottom: 20px;
  }
}
</style>
